import React, { useState } from "react";
import styles from "./style/PacoteList.module.css";
import PacoteReviewsModal from "./PacoteReviewsModal";

type Diferencial = {
  id: number;
  tipo: string;
  descricaoCustomizada?: string | null;
  quantidade: number;
};

type PacoteDTO = {
  id?: number;
  titulo: string;
  descricao: string;
  precoBase: number;
  duracaoEmMinutos: number;
  idiomas: string[];
  idadeMinima: number;
  publicoAlvo: string;
  fotoUrls?: string[];
  videoUrls?: string[];
  motoristaNome?: string;
  servicoNome?: string;
  diferenciais?: Diferencial[];
  status?: string;
};

type Props = {
  pacotes: PacoteDTO[];
  resumos: Record<number, { media: number; total: number }>;
  onEdit: (p: PacoteDTO) => void;
  onDelete: (id: number) => void;
  BASE_URL: string;
};

const PacoteList = ({ pacotes, resumos, onEdit, onDelete, BASE_URL }: Props) => {
  const [openId, setOpenId] = useState<number | null>(null);
  const [modalPacoteId, setModalPacoteId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (pacotes.length === 0) return <p>Nenhum pacote cadastrado ainda.</p>;

  return (
    <div className={styles.list}>
      {pacotes.map((p) => {
        const fotos = p.fotoUrls?.map((url) => `${BASE_URL}${url}`) || [];
        const videos = p.videoUrls?.map((url) => `${BASE_URL}${url}`) || [];
        const isOpen = openId === p.id;

        return (
          <div key={p.id} className={styles.card}>
            {/* Header clicável */}
            <div
              className={styles.cardHeader}
              onClick={() => setOpenId(isOpen ? null : p.id!)}
            >
              <h4>{p.titulo}</h4>

              {/* ⭐ Avaliações no header */}
              <div className={styles.headerRating}>
                {(() => {
                  const resumo = resumos[p.id!];
                  if (!resumo) return <span className={styles.noReview}>Sem avaliações</span>;

                  const fullStars = Math.floor(resumo.media);
                  const hasHalf = resumo.media % 1 >= 0.5;

                  return (
                    <span className={styles.stars}>
                      {Array(fullStars)
                        .fill(0)
                        .map((_, i) => (
                          <i key={i} className="fas fa-star text-warning"></i>
                        ))}
                      {hasHalf && <i className="fas fa-star-half-alt text-warning"></i>}
                      {Array(5 - fullStars - (hasHalf ? 1 : 0))
                        .fill(0)
                        .map((_, i) => (
                          <i key={i} className="far fa-star text-warning"></i>
                        ))}
                      <span className={styles.reviewCount}>
                        ({resumo.total})
                      </span>
                    </span>
                  );
                })()}
              </div>

              <div className={styles.headerRight}>
                <span>R$ {p.precoBase.toFixed(2)}</span>
                <span className={`${styles.arrow} ${isOpen ? styles.open : ""}`}>
                  ▼
                </span>
              </div>
            </div>

            {/* Conteúdo expansível */}
            {isOpen && (
              <div className={styles.cardContent}>
                {/* Linha com Descrição e Diferenciais */}
                <div className={styles.cardRow}>
                  {/* Descrição */}
                  <div className={styles.cardBlock}>
                    <strong>Descrição:</strong>
                    <p>{p.descricao}</p>
                    <p>
                      <strong>Duração:</strong> {p.duracaoEmMinutos} min |{" "}
                      <strong>Idade mínima:</strong> {p.idadeMinima}
                    </p>
                  </div>

                  {/* Diferenciais */}
                  {p.diferenciais && p.diferenciais.length > 0 && (
                    <div className={styles.cardBlock}>
                      <strong>Diferenciais:</strong>
                      <ul>
                        {p.diferenciais.map((d) => (
                          <li key={d.id}>
                            {d.tipo} {d.descricaoCustomizada ? `(${d.descricaoCustomizada})` : ""} - Quantidade: {d.quantidade}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Linha com 2 colunas */}
                <div className={styles.cardRow}>
                  {/* Idiomas e Público-alvo */}
                  <div className={styles.cardBlock}>
                    {p.idiomas.length > 0 && (
                      <p>
                        <strong>Idiomas:</strong> {p.idiomas.join(", ")}
                      </p>
                    )}
                    {p.publicoAlvo && (
                      <p>
                        <strong>Público-alvo:</strong> {p.publicoAlvo}
                      </p>
                    )}
                  </div>

                  {/* Serviço e Motorista */}
                  <div className={styles.cardBlock}>
                    {p.servicoNome && (
                      <p>
                        <strong>Tipo de serviço:</strong> {p.servicoNome}
                      </p>
                    )}
                    {p.servicoNome === "Tuk Tuk" && p.motoristaNome && (
                      <p>
                        <strong>Motorista:</strong> {p.motoristaNome}
                      </p>
                    )}
                    {p.status && (
                      <p>
                        <strong>Status:</strong> {p.status}
                      </p>
                    )}
                  </div>
                </div>

                {/* Fotos e vídeos */}
                {(fotos.length > 0 || videos.length > 0) && (
                  <div className={styles.cardBlock}>
                    <strong>Mídia:</strong>
                    <div className={styles.mediaContainer}>
                      {fotos.map((url, idx) => (
                        <div key={idx} className={styles.fotoCard}>
                          <img src={url} alt={`Foto ${idx + 1}`} className={styles.foto} />
                        </div>
                      ))}
                      {videos.map((url, idx) => (
                        <div key={idx} className={styles.videoCard}>
                          <video src={url} controls className={styles.video} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Botões */}
                <div className={styles.cardButtons}>
                  <button className="edit" onClick={() => onEdit(p)}>
                    Editar
                  </button>
                  <button className="delete" onClick={() => onDelete(p.id!)}>
                    Excluir
                  </button>
                  <button
                    className="view-reviews"
                    onClick={() => {
                      setModalPacoteId(p.id!);
                      setIsModalOpen(true);
                    }}
                  >
                    Ver avaliações
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      <PacoteReviewsModal
        pacoteId={modalPacoteId}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        baseUrl={BASE_URL}
      />
    </div>

  );
};

export default PacoteList;